/**
 * ProtestQueryModal.js - Protest Query Popup Component
 * Modal for selecting protest-related queries and date ranges
 */

import React, { useEffect, useState } from 'react';
import { Icons } from './Icons';
import { config } from '../config';

/**
 * Sanitize location input to prevent prompt injection
 * - Remove special characters that could be used for injection
 * - Limit length
 * - Only allow alphanumeric, spaces, commas, periods, hyphens
 */
const sanitizeLocation = (input) => {
  if (!input) return '';
  
  // Remove any characters that aren't alphanumeric, spaces, commas, periods, hyphens, or apostrophes
  let sanitized = input.replace(/[^a-zA-Z0-9\s,.\-']/g, '');
  
  // Remove multiple spaces
  sanitized = sanitized.replace(/\s+/g, ' ');
  
  // Limit length to 200 characters
  sanitized = sanitized.substring(0, 200);
  
  // Trim whitespace
  sanitized = sanitized.trim();
  
  return sanitized;
};

// Pre-defined query templates with specific keywords for each
const configuredRegions = config.protestReportRegions.length
  ? config.protestReportRegions
  : ['US'];
const REGION_OPTIONS = configuredRegions.map((region) => ({
  value: region,
  label: region,
}));

const QUERY_TEMPLATES = [
  {
    id: 'upcoming_protests',
    title: 'Upcoming Protests & Rallies',
    description: 'Find protests, rallies, strikes, vigils, pickets, walkouts, and days of action',
    keywords: ['protest', 'rally', 'demonstration', 'march', 'strike', 'vigil', 'picket', 'walkout', 'day of action']
  },
  {
    id: 'labor_strikes',
    title: 'Labor Strikes & Union Actions',
    description: 'Find labor strikes and worker protests in major industries',
    keywords: ['strike', 'labor action', 'union protest', 'walkout']
  },
  {
    id: 'ongoing_vigils',
    title: 'Ongoing Vigils & Weekly Rallies',
    description: 'Find regular vigils for various causes',
    keywords: ['vigil', 'rally', 'solidarity march', 'candlelight']
  },
  {
    id: 'major_demonstrations',
    title: 'Major Demonstrations & Days of Action',
    description: 'Find national/global demonstrations and walkouts',
    keywords: ['demonstration', 'walkout', 'boycott', 'day of action']
  }
];

function ProtestQueryModal({ isOpen, onClose, onSubmit, isLoading, progress, initialValues }) {
  const [selectedQuery, setSelectedQuery] = useState(QUERY_TEMPLATES[0].id);
  const [selectedRegion, setSelectedRegion] = useState(REGION_OPTIONS[0].value);
  const [locationScope, setLocationScope] = useState('city');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [location, setLocation] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    const initial = initialValues || {};
    if (initial.query_template) {
      const exists = QUERY_TEMPLATES.some(q => q.id === initial.query_template);
      if (exists) setSelectedQuery(initial.query_template);
    }
    if (initial.region && REGION_OPTIONS.some(r => r.value === initial.region)) {
      setSelectedRegion(initial.region);
    }
    setLocationScope(initial.location_scope === 'country' ? 'country' : 'city');
    setLocation(initial.location || '');
    setStartDate(initial.start_date || '');
    setEndDate(initial.end_date || '');
    setError('');
  }, [isOpen, initialValues]);

  if (!isOpen) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');

    if (!startDate) {
      setError('Please select a start date');
      return;
    }

    if (!endDate) {
      setError('Please select an end date');
      return;
    }

    if (new Date(startDate) > new Date(endDate)) {
      setError('Start date must be before end date');
      return;
    }

    const inclusiveDays = Math.floor(
      (new Date(`${endDate}T00:00:00Z`) - new Date(`${startDate}T00:00:00Z`)) / 86400000
    ) + 1;
    if (inclusiveDays > 30) {
      setError('Date range cannot exceed 30 days');
      return;
    }

    const template = QUERY_TEMPLATES.find(q => q.id === selectedQuery);
    if (!template) {
      setError('Please select a query type');
      return;
    }

    // Sanitize location
    const sanitizedLocation = sanitizeLocation(location);
    if (locationScope === 'country' && !sanitizedLocation) {
      setError('Please enter a country for a country-wide search');
      return;
    }

    // Pass individual parameters for parallel query processing
    onSubmit(
      template.title,
      startDate,
      endDate,
      sanitizedLocation,
      template.keywords,
      selectedRegion,
      locationScope
    );
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget && !isLoading) {
      onClose();
    }
  };

  return (
    <div className="protest-modal-overlay" onClick={handleOverlayClick}>
      <div className="protest-modal">
        <div className="protest-modal-header">
          <div className="protest-modal-title">
            <Icons.Protest />
            <h2>Search Intelligence</h2>
          </div>
          <button 
            className="protest-modal-close" 
            onClick={onClose}
            disabled={isLoading}
            type="button"
          >
            <Icons.Close />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="protest-modal-content">
          <div className="protest-query-section">
            <h3>Select Query Type</h3>
            <div className="protest-query-options">
              {QUERY_TEMPLATES.map(query => (
                <label 
                  key={query.id}
                  className={`protest-query-option ${selectedQuery === query.id ? 'selected' : ''}`}
                >
                  <input
                    type="radio"
                    name="queryType"
                    value={query.id}
                    checked={selectedQuery === query.id}
                    onChange={(e) => setSelectedQuery(e.target.value)}
                    disabled={isLoading}
                  />
                  <div className="protest-query-option-content">
                    <span className="protest-query-option-title">{query.title}</span>
                    <span className="protest-query-option-desc">{query.description}</span>
                  </div>
                </label>
              ))}
            </div>
          </div>

          <div className="protest-location-section">
            <h3>Region</h3>
            <div className="protest-location-field">
              <label htmlFor="protestRegion">Report region</label>
              <select
                id="protestRegion"
                value={selectedRegion}
                onChange={(e) => setSelectedRegion(e.target.value)}
                disabled={isLoading}
              >
                {REGION_OPTIONS.map((r) => (
                  <option key={r.value} value={r.value}>
                    {r.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="protest-location-section">
            <h3>Location Scope</h3>
            <div className="protest-location-field">
              <label htmlFor="locationScope">Search coverage</label>
              <select
                id="locationScope"
                value={locationScope}
                onChange={(e) => setLocationScope(e.target.value)}
                disabled={isLoading}
              >
                <option value="city">City</option>
                <option value="country">Country</option>
              </select>
            </div>
          </div>

          <div className="protest-location-section">
            <h3>{locationScope === 'country' ? 'Country (Required)' : 'City (Optional)'}</h3>
            <div className="protest-location-field">
              <label htmlFor="location">
                <Icons.MapPin />
                {locationScope === 'country' ? 'Country' : 'City or specific location'}
              </label>
              <input
                type="text"
                id="location"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                placeholder={locationScope === 'country' ? 'e.g., Italy' : 'e.g., Rome, Italy'}
                disabled={isLoading}
                maxLength={200}
                autoComplete="off"
                required={locationScope === 'country'}
              />
            </div>
          </div>

          <div className="protest-date-section">
            <h3>Select Date Range</h3>
            <div className="protest-date-inputs">
              <div className="protest-date-field">
                <label htmlFor="startDate">
                  <Icons.Calendar />
                  Start Date
                </label>
                <input
                  type="date"
                  id="startDate"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  disabled={isLoading}
                />
              </div>
              <div className="protest-date-separator">to</div>
              <div className="protest-date-field">
                <label htmlFor="endDate">
                  <Icons.Calendar />
                  End Date
                </label>
                <input
                  type="date"
                  id="endDate"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  disabled={isLoading}
                />
              </div>
            </div>
          </div>

          {error && (
            <div className="protest-error">
              {error}
            </div>
          )}

          <div className="protest-modal-actions">
            <button 
              type="button" 
              className="protest-btn-cancel"
              onClick={onClose}
              disabled={isLoading}
            >
              Cancel
            </button>
            <button 
              type="submit" 
              className="protest-btn-submit"
              disabled={isLoading || !startDate || !endDate || (locationScope === 'country' && !location.trim())}
            >
              {isLoading ? (
                <>
                  <span className="protest-loading-spinner"></span>
                  {progress && progress.total > 0
                    ? `Working ${progress.current}/${progress.total}...`
                    : progress?.phase === 'checking'
                      ? 'Checking report...'
                      : progress?.phase === 'generating'
                        ? 'Generating report...'
                        : 'Generating report...'}
                </>
              ) : (
                <>
                  <Icons.Search />
                  Search
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default ProtestQueryModal;

